import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faCheck, 
    faFilePdf,
    faUpload,
    faMagnifyingGlass,
    faFilter,
    faSpinner,
    faChevronLeft,
    faChevronRight,
    faRotate
} from '@fortawesome/free-solid-svg-icons';
import UploadDocumentModal from './UploadDocumentModal';
import './DocumentBrowser.css';
import 'tippy.js/dist/tippy.css';
import { toast } from 'react-hot-toast';
import { repositoryService } from '../../services/repositoryService';

const DocumentBrowser = ({ 
    onSelect, 
    onClose, 
    onUpload,
    onYearChange,
    onRefresh,
    documentTypes = [],
    workingGroups = [],
    isLoading: isLoadingProp = false 
}) => {
    // Get current year for default
    const currentYear = new Date().getFullYear().toString();
    
    // All state declarations
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [documents, setDocuments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12; // Show 12 documents per page
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [isLoadingLocal, setIsLoadingLocal] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Load documents on mount with current year
    useEffect(() => {
        const loadInitialDocuments = async () => {
            setIsLoadingLocal(true);
            try {
                const items = await repositoryService.getAllItems(null, currentYear);
                setDocuments(items);
            } catch (error) {
                console.error('Failed to load documents:', error);
                toast.error('Failed to load documents');
            } finally {
                setIsLoadingLocal(false);
            }
        };

        loadInitialDocuments();
    }, [currentYear]);

    // Generate years from current year back to 2000 (matching RepositoryBrowser)
    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = ['all']; // Add 'all' as first option
        for (let year = currentYear + 1; year >= 2000; year--) {
            years.push(year.toString());
        }
        return years;
    }, []);

    // Use useMemo for filtered and sorted documents
    const filteredDocuments = useMemo(() => {
        // First filter
        const filtered = documents.filter(doc => {
            const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesYear = selectedYear === 'all' || doc.year === selectedYear;
            return matchesSearch && matchesYear;
        });

        // Then sort by date (newest first)
        return filtered.sort((a, b) => {
            const dateA = new Date(a.modified || a.created || 0);
            const dateB = new Date(b.modified || b.created || 0);
            return dateB - dateA;  // Descending order (newest first)
        });
    }, [documents, searchTerm, selectedYear]);

    // Updated handleYearChange function
    const handleYearChange = async (e) => {  // Note: receiving event object
        const year = e.target.value;
       
        
        setSelectedYear(year);
        setIsLoadingLocal(true);
        
        try {
            const newDocs = await repositoryService.getAllItems(null, year);
  
            
            if (Array.isArray(newDocs)) {
                setDocuments(newDocs);
            } else {
                console.error('Invalid documents response:', newDocs);
                setDocuments([]);
            }

            // Notify parent if needed
            if (onYearChange) {
                onYearChange(year);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
            toast.error('Failed to load documents');
            setDocuments([]);
        } finally {
            setIsLoadingLocal(false);
        }
    };

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedYear]);

    // Pagination calculations
    const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages); // Ensure current page doesn't exceed total pages
    const indexOfLastItem = safeCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredDocuments.slice(indexOfFirstItem, indexOfLastItem);

    // Pagination handlers
    const handlePageChange = (pageNumber) => {
        setCurrentPage(Math.min(pageNumber, totalPages));
    };

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    // Keep your selection effect simple
    useEffect(() => {
        if (selectedDocument) {
            onSelect(selectedDocument);
        }
    }, [selectedDocument, onSelect]);

    // Handlers
    const handleDocumentSelect = (doc) => setSelectedDocument(doc);

    const handleConfirm = () => {

        if (selectedDocument) {
            onSelect(selectedDocument);
            onClose();
        }
    };

    const handleUploadComplete = async (formData) => {
        try {
            if (!onUpload) {
                console.error('Upload handler not provided');
                return;
            }

            // Call the parent's upload handler with the form data and get the uploaded document back
            const uploadedDocument = await onUpload(formData);

            
            // Close the upload modal
            setShowUploadModal(false);

            // Store the document we want to select after refresh
            if (uploadedDocument && uploadedDocument.id) {  // Make sure we have an ID
    
                setPendingSelection(uploadedDocument);
                setSelectedDocument(uploadedDocument);
                setCurrentPage(1); // Reset to first page since new documents appear at top
            } else {
                console.error('Uploaded document missing ID:', uploadedDocument);
            }

            // Refresh the documents list
            if (onRefresh) {
                await onRefresh();
            }

        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload document');
        }
    };

    // Update the upload click handler
    const handleUploadClick = () => {
        if (isLoadingLocal) return;
        setShowUploadModal(true);
    };

    const handleUploadClose = () => setShowUploadModal(false);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatMetadata = (doc) => {
        const parts = [];
        
        // Add year from modified date or created date
        const year = doc.year || 
                    (doc.modified && new Date(doc.modified).getFullYear()) ||
                    (doc.created && new Date(doc.created).getFullYear());
        if (year) parts.push(`📅 ${year}`);
        
        // Add document type with icon
        if (doc.documentType) parts.push(`📄 ${doc.documentType}`);
        
        // Add working group with icon
        if (doc.workingGroup) parts.push(`👥 ${doc.workingGroup}`);

        // Add quarter if available
        if (doc.quarter || doc.qtr) parts.push(`🔄 Q${doc.quarter || doc.qtr}`);

        // Add month if available
        if (doc.month) parts.push(`📅 ${doc.month}`);

        // Add MPKey if available (important business category)
        if (doc.mpKey) parts.push(`🏷️ ${doc.mpKey}`);

        // Add file size if available
        if (doc.size) {
            const sizeInMB = (doc.size / (1024 * 1024)).toFixed(1);
            parts.push(`📦 ${sizeInMB}MB`);
        }

        // Add status/publish state if available
        if (doc.status || doc.publish) {
            const status = doc.status || (doc.publish ? 'Published' : 'Draft');
            parts.push(`${status === 'Published' ? '✅' : '⚠️'} ${status}`);
        }

        return parts.join(' • ') || 'No metadata';
    };

    const renderTooltipContent = (doc) => (
        <div className="document-tooltip">
            <h3>{doc.title}</h3>
            {doc.summary && (
                <p className="document-summary">{doc.summary}</p>
            )}
            <div className="document-tooltip-meta">
                <span>{formatDate(doc.modified || doc.created)}</span>
                <span>{formatMetadata(doc)}</span>
            </div>
        </div>
    );

    // Update the refresh handler to use the selected year
    const handleRefresh = async () => {

        setIsRefreshing(true);
        try {
            // Use the currently selected year
            const newDocs = await repositoryService.getAllItems(null, selectedYear);

            if (onRefresh) {
                onRefresh(newDocs);
            }
        } catch (error) {
            console.error('Refresh error:', error);
            toast.error('Failed to refresh documents');
        } finally {
            setIsRefreshing(false);
        }
    };

    const loadMoreDocuments = async () => {
        if (isLoadingLocal || !hasMore) return;
        
        setIsLoadingLocal(true);
        try {
            const nextPage = page + 1;
            const moreItems = await repositoryService.getAllItems(nextPage, 50);
            setDocuments(prev => [...prev, ...moreItems]);
            setHasMore(moreItems.length === 50);
            setPage(nextPage);
        } catch (error) {
            console.error('Error loading more documents:', error);
        } finally {
            setIsLoadingLocal(false);
        }
    };

    // Add infinite scroll
    useEffect(() => {
        const handleScroll = (e) => {
            const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
            if (bottom) {
                loadMoreDocuments();
            }
        };

        const container = document.querySelector('.document-list');
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    return (
        <div className="document-browser-overlay" onClick={onClose}>
            <div className="document-browser" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <FontAwesomeIcon icon={faFilePdf} className="header-icon" /> 
                        Select PDF Document
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="browser-toolbar">
                    <div className="search-box">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={isLoadingLocal}
                        />
                    </div>

                    <select
                        value={selectedYear}
                        onChange={handleYearChange}
                        className="year-select"
                        disabled={isLoadingLocal}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>
                                {year === 'all' ? 'All Years' : year}
                            </option>
                        ))}
                    </select>

                    <div className="toolbar-actions">
                        <button 
                            className="btn-refresh" 
                            onClick={handleRefresh}
                            disabled={isLoadingLocal || isRefreshing}
                            title="Refresh documents"
                        >
                            <FontAwesomeIcon 
                                icon={faRotate} 
                                className={isRefreshing ? 'fa-spin' : ''} 
                            />
                        </button>
                        <button 
                            className="btn-upload" 
                            onClick={handleUploadClick}
                            disabled={isLoadingLocal}
                        >
                            <FontAwesomeIcon icon={faUpload} /> Upload Document
                        </button>
                    </div>
                </div>

                <div className="modal-body">
                    {isLoadingLocal ? (
                        <div className="loading-state">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        </div>
                    ) : (
                        <div className="documents-grid">
                            {currentItems.map(doc => (
                                <Tippy
                                    key={doc.id}
                                    content={renderTooltipContent(doc)}
                                    placement="right"
                                    duration={[200, 0]}
                                    delay={[500, 0]}
                                    interactive={true}
                                    maxWidth={400}
                                    theme="dark"
                                    appendTo={() => document.body}
                                >
                                    <div
                                        className={`document-item ${selectedDocument?.id === doc.id ? 'selected' : ''}`}
                                        onClick={() => handleDocumentSelect(doc)}
                                    >
                                        <div className="document-icon">
                                            <FontAwesomeIcon icon={faFilePdf} />
                                        </div>
                                        <div className="document-info">
                                            <div className="document-title">{doc.title}</div>
                                            <div className="document-details">
                                                <div className="document-meta">
                                                    {formatMetadata(doc)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Tippy>
                            ))}
                        </div>
                    )}
                </div>

                <div className="document-browser-footer">
                    <button 
                        className="action-button secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button 
                        className="action-button primary"
                        onClick={handleConfirm}
                        disabled={!selectedDocument || isLoadingLocal}
                    >
                        <FontAwesomeIcon icon={faCheck} /> Select Document
                    </button>
                </div>

                <div className="pagination-controls">
                    <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        ‹
                    </button>
                    
                    <span>
                        Page {currentPage} of {Math.ceil(filteredDocuments.length / itemsPerPage)}
                        ({filteredDocuments.length} documents)
                    </span>

                    <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(filteredDocuments.length / itemsPerPage)}
                    >
                        ›
                    </button>
                </div>
            </div>

            {showUploadModal && (
                <UploadDocumentModal
                    onClose={handleUploadClose}
                    onUpload={handleUploadComplete}
                    documentTypes={documentTypes}
                    workingGroups={workingGroups}
                    selectedYear={selectedYear}
                    isLoading={isLoadingLocal}
                />
            )}
        </div>
    );
};

DocumentBrowser.propTypes = {
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpload: PropTypes.func,
    onYearChange: PropTypes.func,
    onRefresh: PropTypes.func,
    documentTypes: PropTypes.array,
    workingGroups: PropTypes.array,
    isLoading: PropTypes.bool
};

export default DocumentBrowser;